import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Nav from "@/components/Nav";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error" });
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState({ phase: "paid", data: res.data });
          return;
        }
      } catch {
        /* keep polling */
      }
      tries += 1;
      if (tries < 30) setTimeout(poll, 2000);
      else setState({ phase: "timeout" });
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="bg-background text-foreground min-h-screen">
      <Nav />
      <main className="max-w-[760px] mx-auto px-6 pt-40 pb-24">
        {state.phase === "polling" && (
          <div data-testid="payment-confirming" className="text-center">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-telemetry" />
            <h1 className="font-display font-black tracking-tight text-4xl mt-8">Confirming your payment…</h1>
            <p className="font-mono text-xs text-muted-foreground tracking-widest mt-4">TALKING TO STRIPE — USUALLY UNDER 5 SECONDS</p>
          </div>
        )}
        {state.phase === "paid" && state.data?.package_id === "upgrade" && (
          <div data-testid="upgrade-success">
            <CheckCircle2 className="w-10 h-10 text-truth" />
            <h1 className="font-display font-black tracking-tight text-5xl mt-8 leading-[0.95]">
              Plan upgraded.
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed mt-4 max-w-lg">
              Your tenant is now on the standard plan — the 10,000-event pilot cap is lifted and
              verification continues unmetered under volume-tiered monthly invoicing. Same API key,
              no code changes.
            </p>
            <div className="flex gap-4 mt-10 flex-wrap">
              <Link
                data-testid="upgrade-geofences-link"
                to="/geofences"
                className="bg-foreground text-background font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-telemetry transition-colors duration-200"
              >
                Back To Zone Editor
              </Link>
              <Link
                data-testid="upgrade-docs-link"
                to="/docs"
                className="border border-foreground font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-foreground hover:text-background transition-colors duration-200"
              >
                Read The Docs
              </Link>
            </div>
          </div>
        )}
        {state.phase === "paid" && state.data?.package_id !== "upgrade" && (
          <div>
            <CheckCircle2 className="w-10 h-10 text-truth" />
            <h1 className="font-display font-black tracking-tight text-5xl mt-8 leading-[0.95]">
              Pilot activated.
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed mt-4 max-w-lg">
              Your tenant is live with 10,000 included verified events. This is your API key —
              it is shown exactly once. Store it now.
            </p>
            {state.data?.tenant_key ? (
              <div data-testid="pilot-key-banner" className="border border-truth bg-card p-6 mt-8">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-truth mb-3">
                  X-GeoVerify-Key — shown once
                </p>
                <p data-testid="pilot-key-value" className="font-mono text-sm bg-muted px-4 py-3 break-all select-all">
                  {state.data.tenant_key}
                </p>
              </div>
            ) : (
              <p className="font-mono text-xs text-muted-foreground mt-8 border border-border px-4 py-3">
                KEY ALREADY REVEALED — CONTACT SUPPORT IF YOU LOST IT
              </p>
            )}
            <div className="flex gap-4 mt-10 flex-wrap">
              <Link
                data-testid="success-geofences-link"
                to="/geofences"
                className="bg-foreground text-background font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-telemetry transition-colors duration-200"
              >
                Draw Your Zones
              </Link>
              <Link
                data-testid="success-docs-link"
                to="/docs"
                className="border border-foreground font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-foreground hover:text-background transition-colors duration-200"
              >
                Read The Docs
              </Link>
            </div>
          </div>
        )}
        {(state.phase === "timeout" || state.phase === "error") && (
          <div data-testid="payment-problem" className="text-center">
            <XCircle className="w-10 h-10 mx-auto text-anomaly" />
            <h1 className="font-display font-black tracking-tight text-4xl mt-8">Taking longer than expected.</h1>
            <p className="text-sm text-muted-foreground mt-4">
              If your card was charged, your key is safe — contact support with your receipt and
              we&apos;ll issue it manually.
            </p>
            <Link to="/" className="inline-block mt-8 border border-foreground font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-foreground hover:text-background transition-colors duration-200">
              Back Home
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default PaymentSuccess;
